import React from "react";
import "./style.scss";
import Title from "../../title";
import ContactDetails from "../../contact-details";
import TOC from "../../toc";
import Pricing from "../../pricing";
import { GroupDocumentProps } from "./types";
import DocumentMetadata from "./DocumentMetadata";

const GroupDocument: React.FC<GroupDocumentProps> = ({
  productId,
  piProperties,
  document,
  aeText,
  company,
}: GroupDocumentProps) => {
  return (
    <div>
      <Title
        id={productId}
        documentId={piProperties?.id}
        title={piProperties?.title}
        piId={piProperties?.piId}
        isBT={document?.isBlackTriangle ?? false}
        isFullPage={false}
        className="productTitle"
      />
      <div className="productContainer">
        <div className="productDetails">
          {document && (
            <DocumentMetadata
              idPostfix={productId}
              companyName={company.name}
              activeIngredient={piProperties.activeIngredient}
              legalCategory={piProperties.legalCategory}
              smpclastUpdated={piProperties.smpclastUpdated}
              pricelastUpdated={piProperties.pricelastUpdated}
              reference={piProperties.reference}
            />
          )}
          <div className="adverseEventText">
            <h3 className="header">Adverse Event Reporting</h3>
            <div
              className="text"
              key={document?.piId}
              dangerouslySetInnerHTML={{
                __html: aeText,
              }}
            />
          </div>
        </div>
        <div className="mainContent">
          <div className="rightSide">
            {document?.toc && (
              <div className="stickyToC">
                <TOC
                  idPostfix={productId}
                  className="tocHeightLimited"
                  htmlData={{
                    __html: getTocHtmlWithProductIdInjected(
                      document.toc,
                      productId
                    ),
                  }}
                />
              </div>
            )}
          </div>
          <div className="tabContainer">
            <div className="smpc-title">
              <h2>SmPC</h2>
            </div>
            <div className="formTabContainer">
              <div className="noticeTitle">
                This information is intended for use by health professionals
              </div>
              {document?.content ? (
                <div
                  className="body"
                  dangerouslySetInnerHTML={{
                    __html: `${addProductIdToSectionIds(
                      document.content,
                      productId
                    )}`,
                  }}
                />
              ) : (
                <div className="body">loading...</div>
              )}
            </div>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/anchor-has-content */}
            <a id={"PRICE" + productId} className="anchor" />
            {piProperties?.price && (
              <Pricing idPostfix={productId} prices={piProperties.price} />
            )}
            <a id={"CONTACT" + productId} className="anchor" />
            <ContactDetails
              idPostfix={productId}
              name={company?.name}
              logoImgSrc={company?.logoUrl}
              details={company?.contactDetail}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const getTocHtmlWithProductIdInjected = (
  htmlString: string,
  productId: string
) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const anchors = doc.querySelectorAll("a[href]");
  anchors.forEach((anchor) => {
    const href = anchor.getAttribute("href");
    if (href!.startsWith("#")) {
      anchor.setAttribute("href", `${href}${productId}`);
    }
  });
  return doc.body.innerHTML;
};

const addProductIdToSectionIds = (htmlString: string, productId: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  // Different for GB/NI documents
  const summaryElements = doc.querySelectorAll("summary, a.anchor");
  summaryElements.forEach((element) => {
    if (element.id) {
      element.id = `${element.id}${productId}`;
    }
  });
  return doc.body.innerHTML;
};

export default GroupDocument;
